import { ElementType } from "../dom-tools/ElementType";
import { HTMLEditor } from "../dom-tools/HTMLEditor";
import { OnClick, OnWheel } from "../dom-tools/EventListeners";
import { TemplatesManager } from "../templates/TemplatesManager";
import { ClassFlag } from "../dom-tools/ClassFlag";
import { ElementAttribute } from "../dom-tools/ElementAttribute";
import { MathX } from "../math/MathX";
import { ScrollLock } from "../dom-actions/ScrollLock";
import { Range } from "../geometry/Range";
import { Vector2 } from "three";
import { TouchGesture } from "../dragging/TouchGesture";
import { ElementSelector } from "../dom-tools/ElementSelector";

export class MediaViewer
{
  static readonly type = new ElementType( "media--viewer" );
  static readonly interaction = new ElementType( "media--viewer-interaction" );
  static readonly startOnClick = new ElementType( "start--on-click" );
  static readonly galleryContent = new ElementType( "gallery--content" );
  static readonly queryElement = new ElementType( "query-element" );
  static readonly infoContent = new ElementType( "info--content" );
  static readonly imageSize = new ElementAttribute( "image-size" );
  

  private static _mediaElements:Element[] = [];
  private static _infoElements:Element[] = [];
  private static _currentIndex:number = 0;

  private static _element:Element;

  static readonly bg = new ElementType( "media--viewer-bg" );  
  static readonly content = new ElementType( "media--viewer-content" );
  static readonly imageContainer = new ElementType( "media--image-container" );
  static readonly infoContainer = new ElementType( "media--viewer-info" );
  static readonly previousButton = new ElementType( "previous--button" );
  static readonly nextButton = new ElementType( "next--button" );
  static readonly closeButton = new ElementType( "close--button" );
  static readonly scaleUpButton = new ElementType( "scale--up--button" );
  static readonly scaleDownButton = new ElementType( "scale--down--button" );

  private static _bg:HTMLElement;
  private static _content:HTMLElement;
  private static _imageContainer:HTMLElement;
  private static _infoContainer:HTMLElement;
  private static _previousButton:HTMLElement;
  private static _nextButton:HTMLElement;
  private static _closeButton:HTMLElement;
  private static _scaleUp:HTMLElement;
  private static _scaleDown:HTMLElement;

  
  static applyOnDocument( templatesManager:TemplatesManager )
  {
    this.ensureMediaViewer( templatesManager );
    MediaViewer.interaction.forAllInDoc( e => this.applyInteractionsOnElement( e ) );
  }

  static attachedListeners = false;

  static open( mediaElements:Element[], starterIndex:number = 0, infoElements:Element[] = [] )
  {
    MediaViewer._mediaElements = mediaElements;
    MediaViewer._infoElements  = infoElements;

    MediaViewer._currentIndex = starterIndex < 0 ? 0 : starterIndex;

    if ( ! this.attachedListeners )
    {
      MediaViewer.attachListeners();
    }

    MediaViewer.loadElement();

    ClassFlag.active.set( this._element );
    ScrollLock.set( true );
  }

  
  static attachListeners()
  {
    MediaViewer.attachedListeners = true;

    let offsetScale = 1;

    OnWheel.add( MediaViewer._content, 
      ( e )=>
      {
        if ( e.deltaY < 0 )
        {
          MediaViewer.scaleUp( e, false );
        }
        else
        {
          MediaViewer.scaleDown( e, false );
        }
      } 
    ) 

    TouchGesture.enable( 
      MediaViewer._content, 
      ( tge )=>
      {        
        // console.log( tge );
        if ( tge.numTouches <= 1 )
        {
          MediaViewer.move( tge.delta );
        }
        else if ( tge.numTouches == 2 )
        {
          if ( tge.touchChanged )
          {
            offsetScale = Math.pow( MediaViewer._scalePower, MediaViewer._scaleLevel );
          }

          MediaViewer.setScale( tge.touchScale * offsetScale );
        }
        
        //
      }
    );

  } 

  static hide()
  {
    let video = MediaViewer._imageContainer.querySelector( "video" ) as HTMLVideoElement;
    
    if ( video )
    {
      video.pause();
    }

    ClassFlag.active.removeFrom( MediaViewer._element );
    ScrollLock.set( false );
    
  }

  

  private static _scalePower = 1.25;
  private static _scaleRange = new Range( -10, 10 );
  private static _scaleLevel = 0;
  private static _translation = new Vector2( 0, 0);

  private static updateTransform()
  {
    let value = Math.pow( MediaViewer._scalePower, MediaViewer._scaleLevel );

    let maxX = window.innerWidth * 0.7 - 50;
    let maxY = window.innerHeight * 0.7 - 50;

    MediaViewer._translation.x = MathX.clamp( MediaViewer._translation.x, -maxX, maxX );
    MediaViewer._translation.y = MathX.clamp( MediaViewer._translation.y, -maxY, maxY );
    
    let tx = MediaViewer._translation.x;
    let ty = MediaViewer._translation.y;

    


    MediaViewer._content.style.transform = `scale3d(${value},${value},${value}) translate3d(${tx}px,${ty}px,0)`;
  }


  private static loadElement()
  {
    let element = this._mediaElements[ this._currentIndex ] as HTMLElement;
    
    this._infoContainer.innerHTML = "";

    if ( this._infoElements.length > 0 && this._infoElements.length > this._currentIndex )
    {
      this._infoContainer.innerHTML = this._infoElements[ this._currentIndex ].innerHTML;
    }

    MediaViewer._imageContainer.innerHTML = "";
    MediaViewer._imageContainer.style.background = "none";

    MediaViewer._scaleLevel = 0;
    MediaViewer._translation.set( 0, 0 );

    MediaViewer.updateTransform();
    
    if ( ElementType.image.is( element ) )
    {
      this.showImage( element );
    }   
    else if ( ElementType.video.is( element ) )
    {
      this.showVideo( element );
    } 
    else 
    {       
      this.showElement( element );
    }
            
  }

  private static showImage( element:Element )
  {
    
    let originalImage = ElementType.image.is( element ) ? element : ElementType.image.query( element );
    let source = ElementAttribute.src.from( originalImage );
    let sizeMode = MediaViewer.imageSize.from( originalImage, "contain" );

    

    MediaViewer._imageContainer.style.backgroundImage = `url(${source})`;
    MediaViewer._imageContainer.style.backgroundPosition = `center`;
    MediaViewer._imageContainer.style.backgroundRepeat = `no-repeat`;
    MediaViewer._imageContainer.style.backgroundSize = `${sizeMode}`;
  }

  private static showVideo( element:Element )
  {
    let source:Element = ElementType.source.query( element );

    if ( ! source && ElementAttribute.src.in( element ) && ElementType.video.is( element ) )
    {
      source = element;
    }

    let videoElement = ElementType.video.create();
    ElementAttribute.controls.to(  videoElement );
    ElementAttribute.src.copy( source, videoElement )
    MediaViewer._imageContainer.appendChild( videoElement );

    videoElement.play();

  }

  private static showElement( element:HTMLElement )
  {
    


    let url = element.style.backgroundImage + "";


    ElementAttribute.style.to( MediaViewer._imageContainer, `background-image:${url}` );
    

    
    
  }

  

  static ensureMediaViewer( templatesManager:TemplatesManager )
  {
    if ( MediaViewer._element )
    {
      return;
    }
   
    let mediaViewerElement = templatesManager.createTemplate( MediaViewer.type.selector, document );
    document.body.appendChild( mediaViewerElement );   

    templatesManager.processElement( mediaViewerElement );

    MediaViewer._element = document.querySelector( MediaViewer.type.selector );

    MediaViewer._bg = MediaViewer.bg.query( mediaViewerElement ) as HTMLElement;
    MediaViewer._content = MediaViewer.content.query( mediaViewerElement ) as HTMLElement;
    
    MediaViewer._imageContainer = MediaViewer.imageContainer.query( mediaViewerElement ) as HTMLElement;
    MediaViewer._infoContainer = MediaViewer.infoContainer.query( mediaViewerElement ) as HTMLElement;
    MediaViewer._previousButton = MediaViewer.previousButton.query( mediaViewerElement ) as HTMLElement;
    MediaViewer._nextButton = MediaViewer.nextButton.query( mediaViewerElement ) as HTMLElement;
    MediaViewer._closeButton = MediaViewer.closeButton.query( mediaViewerElement ) as HTMLElement;
    MediaViewer._scaleUp = MediaViewer.scaleUpButton.query( mediaViewerElement ) as HTMLElement;
    MediaViewer._scaleDown = MediaViewer.scaleDownButton.query( mediaViewerElement ) as HTMLElement;
    
   // OnClick.add(  MediaViewer._bg as HTMLElement, MediaViewer.hide );
    
    OnClick.add(  MediaViewer._closeButton as HTMLElement, MediaViewer.hide );
    
    OnClick.add(  MediaViewer._previousButton as HTMLElement, MediaViewer.previous );

    OnClick.add(  MediaViewer._nextButton as HTMLElement, MediaViewer.next );

    OnClick.add(  MediaViewer._scaleUp as HTMLElement, MediaViewer.scaleUp );
    OnClick.add(  MediaViewer._scaleDown as HTMLElement, MediaViewer.scaleDown );


  }

  static movingTransition = "transform 20ms ease";
  static scalingTransition = "transform 300ms ease";

  static move( dir:Vector2 )
  {
    let amountScaleLevel = Math.pow( MediaViewer._scalePower, - MediaViewer._scaleLevel );
    MediaViewer._translation.add( dir.clone().multiplyScalar( amountScaleLevel ) );

    MediaViewer._content.style.transition = MediaViewer.movingTransition;

    MediaViewer.updateTransform();

  }

  static setScale( scale:number )
  {
    let scaleLevel = Math.log( scale ) / Math.log( MediaViewer._scalePower );
    MediaViewer._scaleLevel = scaleLevel;
    MediaViewer._content.style.transition = MediaViewer.movingTransition;

    MediaViewer.updateTransform();
  }

  static scaleUp( e:MouseEvent|TouchEvent, resetTranslation:boolean = true )
  {
    MediaViewer._scaleLevel = Math.round( MediaViewer._scaleLevel );
    MediaViewer._scaleLevel = MediaViewer._scaleRange.clamp( MediaViewer._scaleLevel + 1 );

    if ( resetTranslation )
    {
      MediaViewer._translation.set( 0, 0 );
    }

    MediaViewer._content.style.transition = MediaViewer.scalingTransition;

    MediaViewer.updateTransform();
    e.preventDefault();
    e.stopPropagation();
  }

  static scaleDown( e:MouseEvent|TouchEvent, resetTranslation:boolean = true )
  {
    MediaViewer._scaleLevel = Math.round( MediaViewer._scaleLevel );
    MediaViewer._scaleLevel = MediaViewer._scaleRange.clamp( MediaViewer._scaleLevel - 1 );

    if ( resetTranslation )
    {
      MediaViewer._translation.set( 0, 0 );
    }
    

    MediaViewer._content.style.transition = MediaViewer.scalingTransition;
    MediaViewer.updateTransform();
    
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

  }

  static previous( e:MouseEvent|TouchEvent )
  {
    MediaViewer._currentIndex = MathX.repeat( MediaViewer._currentIndex - 1, MediaViewer._mediaElements.length );
    MediaViewer.loadElement();

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
  }

  static next( e:MouseEvent|TouchEvent )
  {
    MediaViewer._currentIndex = MathX.repeat( MediaViewer._currentIndex + 1, MediaViewer._mediaElements.length );
    MediaViewer.loadElement();

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
  }

  static applyInteractionsOnElement( element:Element )
  {
    
    let starters:Element[] = [];

    let queryElementSelector = MediaViewer.queryElement.query( element ) ;    

    let queryElement:Document|Element = document ;

    let parentSelectorRegex = /^\s*parent\s*\:/;

    if ( queryElementSelector )
    {
      let selector = queryElementSelector.textContent;
      
      if ( selector === "element" )
      {
        queryElement = element;
      }
      else if ( parentSelectorRegex.test( selector ) )
      {
        let parentSelectorValue = selector.replace( /^\s*parent\s*\:/, "").trim();
        let parentSelector = ElementSelector.parse( parentSelectorValue );
        queryElement = parentSelector.getParent( element );
      }

    }


        

    MediaViewer.startOnClick.forAll( element, 
      ( e )=>
      {

        let elementsList = queryElement.querySelectorAll( e.textContent );
        let elements = HTMLEditor.nodeListToArray( elementsList );

        
        starters = starters.concat( elements );
      } 
    );

    let galleryElements:Element[] = [];    

    MediaViewer.galleryContent.forAll( element, 
      ( e )=>
      {
        
        let elementsList = queryElement.querySelectorAll( e.textContent );
        let elements = HTMLEditor.nodeListToArray( elementsList );
        galleryElements = galleryElements.concat( elements );
      } 
    );

    let infoElements:Element[] = [];

    MediaViewer.infoContent.forAll( element, 
      ( e )=>
      {
        
        let elementsList = queryElement.querySelectorAll( e.textContent );
        let elements = HTMLEditor.nodeListToArray( elementsList );
        infoElements = infoElements.concat( elements );
      } 
    );

    

    starters.forEach(
      ( s )=>
      {
        OnClick.add( s as HTMLElement, 
          ( clickEvent )=>
          {
            let index = starters.indexOf( s );
            MediaViewer.open( galleryElements, index, infoElements );
          }
        )
      }
    )
  }
}